import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { pathBuilder } from "./path-builder";
import i18n from "i18next";
import { VitalSigns } from "@binah/web-sdk";
import { captureException } from "../application-monitoring-adapter";

const supportedVitalSigns = [
  "heartRate",
  "pulseRate",
  "oxygenSaturation",
  "breathingRate",
  "respirationRate",
  "sdnn",
  "stressLevel",
  "stressIndex",
  "bloodPressure",
  "rri",
  "meanRri",
  "rmssd",
  "sd1",
  "sd2",
  "prq",
  "pnsIndex",
  "pnsZone",
  "snsIndex",
  "snsZone",
  "wellnessLevel",
  "wellnessIndex",
  "lfhf",
  "hemoglobin",
  "hemoglobinA1c",
];

const vitalSignsMapping: { [key: string]: { name: string } } = {
  pulseRate: {
    name: "heartRate",
  },
  respirationRate: {
    name: "breathingRate",
  },
};

export function cleanseVitalSigns(vitalSigns: any) {
  let result: any = {};
  for (const key in vitalSigns) {
    if (vitalSignsMapping[key]) {
      result[vitalSignsMapping[key].name] = vitalSigns[key];
    } else if (supportedVitalSigns.indexOf(key) > -1) {
      result[key] = vitalSigns[key];
    }
  }
  if (!Object.keys(result).length) {
    result = null;
  }
  return result;
}

interface RequestBaseOptions {
  signal: AbortSignal;
}

export type HealthCheckQuestionnaireData = {
  weight: { value: string };
  height: { unit: string; value: number };
};

const baseURI = String(import.meta.env.VITE_APP_UNU_HEALTH_API_BASE_URI) || "";

interface AxiosRequestConfigWithCustomProps extends AxiosRequestConfig {
  isAttemptingToGetNewAccessToken: boolean;
}

const paths = pathBuilder({
  baseURI: baseURI,
});

const noCacheHeaders = {
  "Cache-Control": "no-cache",
  Pragma: "no-cache",
  Expires: "0",
};

const axiosInstance = axios.create({
  baseURL: baseURI,
});

axiosInstance.interceptors.request.use(function (config: any) {
  config.headers["Accept-Language"] = i18n.resolvedLanguage;

  return config;
});

function responseInterceptorForSuccessfulResponses(response: any) {
  if (response.data?.isError === true || response.data?.isSuccess === false) {
    return Promise.reject(response);
  }
  return response;
}

async function responseInterceptorForUnsuccessfulResponses(
  error: AxiosError<{ error: { message: string } }>
) {
  const currentRequest = error.config as AxiosRequestConfigWithCustomProps;
  const currentResponseStatus = error.response?.status || 500;

  const modifiedResponse = {
    status: currentResponseStatus,
    data: error.response?.data?.error || {
      message: "",
    },
  };

  if (currentResponseStatus === 500) {
    modifiedResponse.data.message = "unknown_error";
  }

  if (error.response?.status === 500) {
    const errorData = {
      status: error.response?.status,
      message: error.message || "",
      data: error.response?.data?.error || "",
    };

    captureException(
      `Error experienced on ${currentRequest.url}: ${JSON.stringify(errorData)}`
    );
  }
  return Promise.reject(modifiedResponse);
}

axiosInstance.interceptors.response.use(
  responseInterceptorForSuccessfulResponses,
  responseInterceptorForUnsuccessfulResponses
);

export interface Screenshot {
  filename: string;
  data: string;
}

export function submitFaceScanData(
  data: {
    healthCheckQuestionnaireData: HealthCheckQuestionnaireData | null;
    images: Screenshot[];
    quoteId: string | null;
    vitalSigns: VitalSigns | null;
  },
  options?: RequestBaseOptions
): Promise<any> {
  const { healthCheckQuestionnaireData, images, quoteId, vitalSigns } = data;
  const height = healthCheckQuestionnaireData?.height;
  const weight = healthCheckQuestionnaireData?.weight;

  return axiosInstance({
    method: "post",
    url: paths.submitFaceScanData,
    headers: {
      ...noCacheHeaders,
      Authorization: `ApiKey ${String(import.meta.env.VITE_APP_UNU_HEALTH_API_KEY)}`,
    },
    signal: options?.signal,
    data: {
      height,
      images,
      QuoteId: quoteId,
      vitalSigns: cleanseVitalSigns(vitalSigns),
      weight,
    },
  })
    .then((res: any) => {
      return res.data;
    })
    .catch((error) => {
      throw new Error(error.data);
    });
}

function getI18nTranslationKeyFromMessage(
  message: string | null | undefined
): string {
  if (!message) {
    message = "Unknown error";
  }

  if (message.indexOf("Member has been locked out for") > -1) {
    message = "User temporarily locked out";
  }

  const normalisedMessage = message.toLowerCase().split(" ").join("_");
  const translationKey = `UnuHealthAPI.messages.${normalisedMessage}`;

  return i18n.exists(translationKey) ? translationKey : message;
}

export function getI18nTranslationKeysFromMessages(
  messages: Array<string | null | undefined>
): Array<string> {
  return messages.map((message) => getI18nTranslationKeyFromMessage(message));
}

export interface LatestContractResponse {
  description: string;
  version: number;
  fileUri: string;
  url: string;
  hasAccepted: boolean;
}

export function getLatestContract(
  QuoteId: string,
  options?: RequestBaseOptions
): Promise<any> {
  return axiosInstance({
    method: "get",
    url: paths.latestContract.replace("{QuoteId}", QuoteId),
    headers: {
      ...noCacheHeaders,
      Authorization: `ApiKey ${String(import.meta.env.VITE_APP_UNU_HEALTH_API_KEY)}`,
    },
    signal: options?.signal,
  })
    .then((res: any) => {
      return res.data;
    })
    .catch((error) => {
      throw new Error(error.data);
    });
}

export function acceptTermsAndConditions(
  quoteId: string,
  options?: RequestBaseOptions
): Promise<any> {
  return axiosInstance({
    method: "post",
    url: paths.acceptTermsAndConditions,
    headers: {
      ...noCacheHeaders,
      Authorization: `ApiKey ${String(import.meta.env.VITE_APP_UNU_HEALTH_API_KEY)}`,
    },
    signal: options?.signal,
    data: {
      quoteId: quoteId,
    },
  })
    .then((res: any) => {
      return res.data;
    })
    .catch((error) => {
      if (error.response && error.response.data) {
        throw new Error(JSON.stringify(error.response.data));
      }
      throw new Error(error.message);
    });
}
